import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, UserPlus, X } from '@phosphor-icons/react';
import {
  CAUSE_CATEGORY,
  CAUSE_LABEL,
  PRIORITY,
  PRIORITY_LABEL,
  REJECT_REASON_LABEL,
  STATUS_LABEL,
  isViewableFile,
  type CauseCategory,
  type Priority,
  type RejectReason,
  type TicketStatus,
  type TransitionTarget,
  type Visibility,
} from '@helpdesk/shared';
import { assertDocumentSize, deleteJson, getJson, patchJson, postJson, uploadFile } from '../api/client.ts';
import { meQuery, ticketQuery, workersQuery } from '../api/queries.ts';
import type { Contact, MachineListItem, TicketDetail } from '../api/types.ts';
import { AssignDialog, Avatar, CollaboratorDialog, TicketChangedNote, WaitingFor, takeTicket, ticketChanges } from '../components/assign.tsx';
import { OriginBadge, PriorityBadge, StatusBadge } from '../components/badges.tsx';
import { FileViewer, downloadUrl } from '../components/file-viewer.tsx';
import { Badge, Button, Checkbox, ErrorNote, Field, Input, Modal, Notice, Select, Spinner, Tag, Textarea } from '../components/ui.tsx';
import { fmtAge, fmtDate, fmtDateTime, fromLocalInput, toLocalInput } from '../lib/format.ts';
import { resizePhoto } from '../lib/image.ts';
import { hasAtWord, resolveMentions } from '../lib/mentions.ts';
import { useAction } from '../lib/use-action.ts';

/**
 * Ticketseite (§10.1): Kopf, große Knöpfe NUR für Übergänge, die der Server
 * erlaubt (`permissions.transitions` kommt aus `state.ts`), Zeitleiste, Fotos,
 * Kommentar intern oder an den Melder, Mail-Inhalte im Sandbox-Frame.
 */
type Dialog = TransitionTarget | 'start';

const ACTION_LABEL: Record<TransitionTarget, string> = {
  accepted: 'Annehmen',
  in_progress: 'Weiterarbeiten',
  waiting_part: 'Wartet auf Teil',
  waiting_external: 'Wartet auf Dienstleister',
  waiting_reporter: 'Rückfrage an Melder',
  resolved: 'Behoben',
  closed: 'Abschließen',
  rejected: 'Ablehnen',
  reopen: 'Wieder öffnen',
};

/** Reihenfolge der Knöpfe: der nächste Arbeitsschritt zuerst, Ablehnen zuletzt. */
const ACTION_ORDER: TransitionTarget[] = ['accepted', 'in_progress', 'resolved', 'closed', 'waiting_part', 'waiting_external', 'waiting_reporter', 'reopen', 'rejected'];

export function TicketPage() {
  const { number } = useParams({ from: '/app/tickets/$number' });
  const ticket = useQuery(ticketQuery(number));
  const me = useQuery(meQuery);
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const { run, busy, error, setError } = useAction();
  const [notice, setNotice] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);

  if (ticket.isPending) return <Spinner />;
  if (ticket.isError) return <ErrorNote error={ticket.error} />;
  const t = ticket.data;
  const p = t.permissions;
  const worker = p.canCommentInternal;
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['ticket', number] });
    await qc.invalidateQueries({ queryKey: ['tickets'] });
  };

  const transition = async (body: Record<string, unknown>) => {
    setError(null);
    try {
      await postJson(`/api/tickets/${t.id}/transition`, { ...body, version: t.version });
      await refresh();
    } catch (err) {
      setError(err);
      await refresh();
    }
  };

  const openDialog = (kind: Dialog) => {
    setError(null);
    setDialog(kind);
  };

  const confirm = async (action: 'running' | 'not_running', comment?: string) => {
    setError(null);
    try {
      await postJson(`/api/tickets/${t.id}/confirm`, { action, ...(comment ? { comment } : {}) });
      setNotice(action === 'running' ? 'Danke, das Ticket ist abgeschlossen.' : 'Danke, die Instandhaltung schaut erneut.');
      await refresh();
    } catch (err) {
      setError(err);
      await refresh();
    }
  };

  const take = async () => {
    setError(null);
    try {
      await takeTicket(t);
      await refresh();
    } catch (err) {
      setError(err);
      await refresh();
    }
  };

  const unblockReply = async () => {
    setError(null);
    try {
      await postJson(`/api/tickets/${t.id}/reply-unblock`, { version: t.version });
      await refresh();
    } catch (err) {
      setError(err);
      await refresh();
    }
  };

  const isReporterOnly = !worker && t.reporter.isSelf;
  const buttons = p.transitions
    .filter((x) => !(isReporterOnly && (x === 'closed' || x === 'in_progress')))
    .sort((a, b) => ACTION_ORDER.indexOf(a) - ACTION_ORDER.indexOf(b));
  // Bei neuen Tickets ist „Annehmen“ das Übernehmen – mit Prüfung der Maschine.
  const canTake = p.canTake && !buttons.includes('accepted');

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/tickets" search={{}} className="ad-link inline-flex min-h-10 items-center gap-1.5 text-[15px] font-medium no-underline">
          <ArrowLeft size={18} /> Zur Liste
        </Link>
      </div>

      <header className={`ad-card space-y-3 ${t.priority === 'p1' ? 'border-l-4 border-l-arnold-red' : ''}`}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-lg font-semibold text-steel-700">{t.display}</span>
          <PriorityBadge priority={t.priority} />
          <StatusBadge status={t.status} />
          {t.needsReview ? <span className="ad-badge bg-steel-200 text-steel-800">Prüfen</span> : null}
          {t.lockedOut ? <span className="ad-badge bg-steel-800 text-white">Maschine gesichert</span> : null}
          <OriginBadge origin={t.origin} />
          {t.reporter.replyBlocked ? <Badge tone="warn">Mail an Melder gesperrt</Badge> : null}
          {p.canUnblockReply ? (
            <Button size="sm" variant="ghost" onClick={() => void unblockReply()}>
              Wieder zulassen
            </Button>
          ) : null}
        </div>
        <h1 className="text-[22px] normal-case">{t.title}</h1>
        <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <Row label="Maschine">
            {t.machine ? (
              <Link to="/maschinen/$id" params={{ id: t.machine.id }} className="inline-flex items-center gap-2">
                {t.machine.photo ? (
                  <img src={t.machine.photo.thumbUrl} alt="" className="h-10 w-12 shrink-0 border border-[var(--border-default)] object-cover" />
                ) : null}
                <span>
                  {t.machine.name} ({t.machine.inventoryNo}) · {t.machine.areaName}
                </span>
              </Link>
            ) : t.noMachine ? (
              'ohne Maschine (bestätigt)'
            ) : (
              'noch keine, bitte beim Annehmen nachtragen'
            )}
          </Row>
          <Row label="Standort">{t.site?.name ?? '-'}</Row>
          <Row label="Gemeldet">
            {fmtDateTime(t.createdAt)} ({fmtAge(t.createdAt)}) von {t.reporter.name}
            {t.reporter.email ? ` · ${t.reporter.email}` : ''}
          </Row>
          {t.waitingNote || t.waitingUntil ? (
            <Row label="Wartet">
              {t.waitingNote ?? ''}
              {t.waitingUntil ? ` · erwartet ${fmtDate(t.waitingUntil)}` : ''}
              {t.waitingContact ? ` · ${t.waitingContact.company ?? t.waitingContact.email}` : ''}
            </Row>
          ) : null}
          {t.resolvedAt ? (
            <Row label="Behoben">
              {fmtDateTime(t.resolvedAt)}
              {t.causeCategory ? ` · Ursache: ${CAUSE_LABEL[t.causeCategory]}` : ''}
              {t.machineRunningAt ? ` · läuft wieder seit ${fmtDateTime(t.machineRunningAt)}` : ''}
            </Row>
          ) : null}
          {t.duplicateOf ? (
            <Row label="Dublette von">
              <Link to="/tickets/$number" params={{ number: t.duplicateOf }}>
                {t.duplicateOf}
              </Link>
            </Row>
          ) : null}
          {t.reporter.external ? (
            <Row label="Externer Melder">
              {t.reporter.external.company ?? t.reporter.email} · {t.reporter.external.trusted ? 'freigegeben' : 'nicht freigegeben, keine Mails'}
              {p.canTrustContact && !t.reporter.external.trusted ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await patchJson(`/api/contacts/${t.reporter.external!.id}`, { trusted: true });
                      await refresh();
                    })
                  }
                >
                  Freigeben
                </Button>
              ) : null}
            </Row>
          ) : null}
        </dl>
        {worker ? (
          <div className="grid grid-cols-2 border-t border-[var(--border-default)] pt-3 sm:grid-cols-4">
            <Prop label="Wartet auf">
              <WaitingFor status={t.status} hasAssignee={Boolean(t.assignee)} />
            </Prop>
            <Prop label="Team">{t.team?.name ?? '-'}</Prop>
            <Prop label="Bearbeiter">{t.assignee ? t.assignee.displayName : <span className="text-[var(--text-muted)] italic">Nicht zugewiesen</span>}</Prop>
            <Prop label="Geändert">
              <span title={fmtDateTime(t.updatedAt)}>{fmtAge(t.updatedAt)}</span>
            </Prop>
          </div>
        ) : null}
        {t.machine?.safetyNote ? (
          <div className="border-l-4 border-arnold-red bg-arnold-red-tint px-3 py-2 text-sm">
            <strong>Sicherheitshinweis:</strong> {t.machine.safetyNote}
          </div>
        ) : null}
      </header>

      {p.isCollaborator && !p.canAssign ? (
        <Notice>Sie sind zu diesem Ticket hinzugezogen. Annahme und Zuweisung liegen bei der Instandhaltung {t.site?.name ?? 'des Werks'}.</Notice>
      ) : null}
      {notice ? <Notice tone="ok">{notice}</Notice> : null}
      {error ? <ErrorNote error={error} /> : null}

      {isReporterOnly && t.status === 'resolved' ? (
        <div className="ad-card space-y-3">
          <p className="font-semibold">Die Instandhaltung meldet: behoben. Läuft die Maschine wieder?</p>
          <div className="flex flex-wrap gap-2">
            <Button size="lg" onClick={() => void confirm('running')}>
              Läuft wieder
            </Button>
            <Button size="lg" variant="outline" onClick={() => void confirm('not_running')}>
              Läuft nicht
            </Button>
          </div>
        </div>
      ) : null}

      {buttons.length || canTake || p.canAssign ? (
        <div className="flex flex-wrap gap-2">
          {buttons.map((target) => (
            <Button
              key={target}
              size="lg"
              variant={target === 'resolved' || target === 'accepted' ? 'primary' : target === 'in_progress' || target === 'closed' ? 'secondary' : 'outline'}
              onClick={() => {
                if (target === 'closed') void transition({ to: 'closed' });
                else if (target === 'in_progress' && t.status === 'accepted' && t.priority !== 'p1') void transition({ to: 'in_progress' });
                else if (target === 'in_progress' && t.status === 'accepted') openDialog('start');
                else if (target === 'in_progress' && t.status !== 'resolved') void transition({ to: 'in_progress' });
                else if (target === 'accepted' && !t.needsReview && t.machine && me.data?.user.role !== 'lead') void transition({ to: 'accepted' });
                else openDialog(target);
              }}
            >
              {target === 'in_progress' && t.status === 'accepted'
                ? 'Arbeit beginnen'
                : target === 'in_progress' && t.status === 'resolved'
                  ? 'Weiterarbeiten'
                  : target === 'closed'
                    ? 'Abschließen'
                    : ACTION_LABEL[target]}
            </Button>
          ))}
          {canTake || p.canAssign ? (
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              {canTake ? (
                <Button size="lg" variant={t.assignee ? 'outline' : 'primary'} onClick={() => void take()}>
                  Übernehmen
                </Button>
              ) : null}
              {p.canAssign ? (
                <Button size="lg" variant="outline" onClick={() => setAssignOpen(true)}>
                  Zuweisen
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {p.reporterUnreachable ? <Notice>Rückfrage nicht möglich: Der Melder ist nicht erreichbar. Bitte einen internen Kommentar schreiben.</Notice> : null}

      {/* Ein Absenden schließt nur seinen eigenen Dialog: Wer abbricht, kann bis zur Antwort schon den nächsten geöffnet haben. */}
      {dialog ? (
        <TransitionDialogs
          key={dialog}
          ticket={t}
          dialog={dialog}
          onClose={() => setDialog((open) => (open === dialog ? null : open))}
          onDone={refresh}
          meId={me.data?.user.id ?? ''}
          isLead={me.data?.user.role === 'lead'}
        />
      ) : null}
      {assignOpen && t.site ? (
        <AssignDialog
          tickets={[{ id: t.id, version: t.version, display: t.display, siteId: t.site.id, teamId: t.team?.id ?? null, assigneeId: t.assignee?.id ?? null, status: t.status, detail: t }]}
          onClose={() => setAssignOpen(false)}
          onDone={refresh}
        />
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          {p.canComment ? <CommentBox ticket={t} onDone={refresh} /> : null}
          <Timeline ticket={t} />
        </div>
        <aside className="space-y-5">
          {/* Beide Karten prüfen ihre Rechte selbst: Hingen sie an `worker`, nähme das Neuladen nach einer Ablehnung sie samt Fehler von der Seite. */}
          <Participants
            key={t.id}
            ticket={t}
            meId={me.data?.user.id ?? ''}
            onTake={canTake ? () => takeTicket(t) : undefined}
            onAssign={p.canAssign ? () => setAssignOpen(true) : undefined}
            onChanged={refresh}
          />
          <Photos ticket={t} onChange={refresh} />
          {/* Der Router behält die Seite beim Sprung zum nächsten Ticket: Der Schlüssel beendet Eingaben und Fehler mit ihrem Ticket. */}
          <WorkerControls key={t.id} ticket={t} onChange={refresh} />
        </aside>
      </div>
    </div>
  );
}

function Prop({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 py-1 pr-3">
      <div className="ad-caption">{label}</div>
      <div className="mt-0.5 truncate text-[14px] text-steel-900">{children}</div>
    </div>
  );
}

/** Beteiligte wie in InvGate: Melder, Team, Bearbeiter – mit Übernehmen/Zuweisen. */
function Participants({
  ticket: t,
  meId,
  onTake,
  onAssign,
  onChanged,
}: {
  ticket: TicketDetail;
  meId: string;
  onTake?: (() => Promise<unknown>) | undefined;
  onAssign?: (() => void) | undefined;
  onChanged: () => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const { run, busy, error } = useAction();
  const p = t.permissions;
  const act = (action: () => Promise<unknown>) => run(() => action().finally(onChanged));
  // Ohne Rechte am Ticket sieht der Melder nichts aus dieser Karte, nur den Fehler der eigenen Aktion.
  if (!p.canCommentInternal) {
    return error ? (
      <section className="ad-card space-y-4">
        <h2 className="ad-section-title">Beteiligte</h2>
        <ErrorNote error={error} />
      </section>
    ) : null;
  }
  return (
    <section className="ad-card space-y-4">
      <h2 className="ad-section-title">Beteiligte</h2>
      <Person label="Melder" name={t.reporter.name ?? 'unbekannt'} sub={t.reporter.email ?? (t.reporter.external ? (t.reporter.external.company ?? undefined) : undefined)} />
      <Person label="Team" name={t.team?.name ?? 'kein Team'} kind="team" sub={t.site?.name} />
      <Person label="Bearbeiter" name={t.assignee?.displayName ?? 'Nicht zugewiesen'} kind={t.assignee ? 'person' : 'none'} />
      {t.collaborators.length || p.canAddCollaborator ? (
        <div className="space-y-2 border-t border-[var(--border-default)] pt-3">
          <div className="ad-caption">Hinzugezogen</div>
          {t.collaborators.map((c) => (
            <div key={c.id} className="flex items-center gap-3">
              <Avatar name={c.displayName} size={30} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] text-steel-900">{c.displayName}</div>
                <div className="text-[13px] text-[var(--text-muted)]">{c.siteCodes.join(', ') || 'ohne Werk'}</div>
              </div>
              {p.canRemoveCollaborator || c.id === meId ? (
                <button
                  type="button"
                  className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center text-steel-600 hover:bg-steel-100 hover:text-steel-900"
                  aria-label={c.id === meId ? 'Mich wieder herausnehmen' : `${c.displayName} entfernen`}
                  title={c.id === meId ? 'Mich herausnehmen' : 'Entfernen'}
                  disabled={busy}
                  onClick={() => void act(() => deleteJson(`/api/tickets/${t.id}/collaborators/${c.id}`, { version: t.version }))}
                >
                  <X size={18} />
                </button>
              ) : null}
            </div>
          ))}
          {p.canAddCollaborator ? (
            <Button size="sm" variant="ghost" className="-ml-3" onClick={() => setAdding(true)}>
              <UserPlus size={18} />
              Kollegen hinzuziehen
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? <ErrorNote error={error} /> : null}
      {adding && t.site ? (
        <CollaboratorDialog
          ticket={{ id: t.id, version: t.version, display: t.display, siteId: t.site.id }}
          exclude={[meId, ...(t.assignee ? [t.assignee.id] : []), ...t.collaborators.map((c) => c.id)]}
          onClose={() => setAdding(false)}
          onDone={onChanged}
        />
      ) : null}
      {onTake || onAssign ? (
        <div className="flex flex-wrap gap-2 border-t border-[var(--border-default)] pt-3">
          {onTake ? (
            <Button size="sm" disabled={busy} onClick={() => void act(onTake)}>
              Übernehmen
            </Button>
          ) : null}
          {onAssign ? (
            <Button size="sm" variant="outline" onClick={onAssign}>
              Zuweisen
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function Person({ label, name, sub, kind = 'person' }: { label: string; name: string; sub?: string | undefined; kind?: 'person' | 'team' | 'none' }) {
  return (
    <div className="flex items-center gap-3">
      <Avatar name={name} kind={kind} size={34} />
      <div className="min-w-0">
        <div className="ad-caption">{label}</div>
        <div className={`truncate text-[14px] ${kind === 'none' ? 'text-[var(--text-muted)] italic' : 'text-steel-900'}`}>{name}</div>
        {sub ? <div className="truncate text-[13px] text-[var(--text-muted)]">{sub}</div> : null}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-[var(--text-muted)]">{label}:</dt>
      <dd className="m-0 text-steel-900">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Übergänge
// ---------------------------------------------------------------------------

/** Bietet an, was der Server annimmt: Instandhalter wählen in ihren Werken, ein behobenes Ticket bleibt in seinem. */
function MachinePicker({ ticket: t, value, onChange }: { ticket: TicketDetail; value: MachineListItem | null; onChange: (machine: MachineListItem) => void }) {
  const me = useQuery(meQuery);
  const [q, setQ] = useState('');
  const user = me.data?.user;
  const sites = t.status === 'resolved' && t.site ? [t.site.id] : user?.role === 'lead' ? null : (user?.siteIds ?? []);
  const results = useQuery({
    queryKey: ['machines', 'picker', q, sites],
    queryFn: () => getJson<{ items: MachineListItem[] }>('/api/machines', { q, pageSize: 8, siteId: sites?.length === 1 ? sites[0] : undefined }),
    enabled: q.trim().length >= 2,
  });
  const items = results.data?.items.filter((m) => !sites || sites.includes(m.siteId));
  return (
    <div className="space-y-2">
      <Input label="Maschine suchen" value={q} onChange={setQ} placeholder="Name oder Inventarnummer" />
      {items?.map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => onChange(m)}
          className={`flex w-full cursor-pointer items-center gap-2 border px-3 py-2 text-left text-sm ${value?.id === m.id ? 'border-arnold-red bg-arnold-red-tint' : 'border-[var(--border-default)] bg-white'}`}
        >
          <span className="font-mono">{m.inventoryNo}</span> {m.name} <span className="ml-auto text-[var(--text-muted)]">{m.siteCode}</span>
        </button>
      ))}
      {value && t.site && value.siteId !== t.site.id ? (
        <Notice tone="warn">
          Diese Maschine steht im Werk {value.siteCode}. Das Ticket wechselt mit dem Team dorthin, das Team dort wird benachrichtigt.
          {t.assignee ? ` Gehört der Bearbeiter nicht zu diesem Werk, gibt er das Ticket ab und es steht dort wieder auf „${STATUS_LABEL.new}“.` : ''}
        </Notice>
      ) : null}
    </div>
  );
}

/** Dringende Tickets verlangen beim Abschluss die Laufzeit und gelten zunächst als Produktionsstillstand. */
const isUrgent = (priority: Priority) => priority === 'p1' || priority === 'p2';

/** Wird je geöffnetem Dialog frisch montiert: Eingaben, Fehler und Stand enden mit dem Dialog. */
function TransitionDialogs({
  ticket: t,
  dialog,
  onClose,
  onDone,
  meId,
  isLead,
}: {
  ticket: TicketDetail;
  dialog: Dialog;
  onClose: () => void;
  onDone: () => Promise<void>;
  meId: string;
  isLead: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Der Refetch tauscht `t` unter dem Dialog aus, gesendet wird die Version, auf der die Person entscheidet.
  const [seen, setSeen] = useState(t);
  // Beim Absenden hebt auch der eigene Erfolg die Version, das ist keine fremde Änderung.
  const changed = !busy && t.version !== seen.version;
  const locked = busy || changed;
  const run = async (body: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await postJson(`/api/tickets/${t.id}/transition`, { ...body, version: seen.version });
      await onDone();
      onClose();
    } catch (err) {
      setError(err);
      await onDone();
    }
    setBusy(false);
  };
  // Formularzustände
  const [machine, setMachine] = useState<MachineListItem | null>(null);
  const [noMachine, setNoMachine] = useState(false);
  const [assignee, setAssignee] = useState(meId);
  const [lockedOut, setLockedOut] = useState(true);
  const [note, setNote] = useState('');
  const [infor, setInfor] = useState('');
  const [until, setUntil] = useState('');
  const [contactId, setContactId] = useState('');
  const [question, setQuestion] = useState('');
  const [cause, setCause] = useState<CauseCategory>('mechanical');
  const [resolution, setResolution] = useState('');
  const [stop, setStop] = useState(isUrgent(t.priority));
  const [runningAt, setRunningAt] = useState(toLocalInput(new Date()));
  const [releasedBy, setReleasedBy] = useState(meId);
  const [sparePart, setSparePart] = useState('');
  const [pickedReason, setReason] = useState<RejectReason>('not_a_fault');
  const [comment, setComment] = useState('');
  const [dupOf, setDupOf] = useState('');
  const dupNumber = Number(dupOf.replace(/\D/g, ''));
  // Der Refetch kann die Auswahl verkleinern, gesendet wird nur ein Grund, den die Liste auch zeigt.
  const rejectReasons = t.permissions.rejectReasons;
  const reason = rejectReasons.includes(pickedReason) ? pickedReason : (rejectReasons[0] ?? pickedReason);

  const targetSiteId = machine?.siteId ?? t.site?.id;
  const workers = useQuery({ ...workersQuery(targetSiteId), enabled: dialog === 'accepted' || dialog === 'resolved' });
  const contacts = useQuery({ queryKey: ['contacts'], queryFn: () => getJson<Contact[]>('/api/contacts'), enabled: dialog === 'waiting_external' });
  const workerOptions = (workers.data ?? []).map((w) => ({ value: w.id, label: w.displayName }));
  const needsMachine = t.needsReview || (!t.machine && !t.noMachine);
  const modal = { open: true, onClose, error: changed ? null : error };
  const footerStart = (
    <>
      {changed ? (
        <TicketChangedNote
          changes={ticketChanges(seen, t)}
          onSeen={() => {
            // Der Haken folgt der Priorität, solange die Person ihn nicht selbst umgestellt hat.
            if (stop === isUrgent(seen.priority)) setStop(isUrgent(t.priority));
            setSeen(t);
            setError(null);
          }}
        />
      ) : null}
      <Button variant="ghost" onClick={onClose}>
        Abbrechen
      </Button>
    </>
  );

  switch (dialog) {
    case 'accepted':
      return (
        <Modal
          {...modal}
          title="Annehmen"
          footer={
            <>
              {footerStart}
              <Button
                disabled={locked || (needsMachine && !t.machine && !machine && !noMachine)}
                onClick={() =>
                  void run({
                    to: 'accepted',
                    ...(isLead && assignee !== meId ? { assigneeUserId: assignee } : {}),
                    ...(machine ? { machineId: machine.id } : {}),
                    ...(noMachine ? { confirmNoMachine: true } : {}),
                  })
                }
              >
                Annehmen
              </Button>
            </>
          }
        >
          {needsMachine ? (
            <>
              <Notice>Diese Meldung ist noch ungeprüft. {t.machine ? 'Maschine bestätigen oder ändern.' : 'Bitte die Maschine nachtragen.'}</Notice>
              <MachinePicker
                ticket={t}
                value={machine}
                onChange={(picked) => {
                  // Die Personenliste folgt dem Werk der Maschine, die bisherige Wahl stünde nicht mehr darin.
                  if (picked.siteId !== targetSiteId) setAssignee(meId);
                  setMachine(picked);
                }}
              />
              {!t.machine ? <Checkbox label="ohne Maschine bestätigen (Gebäude, Infrastruktur)" checked={noMachine} onChange={setNoMachine} /> : null}
            </>
          ) : null}
          {isLead ? <Select label="Bearbeiter" value={assignee} onChange={setAssignee} options={workerOptions.length ? workerOptions : [{ value: meId, label: 'Ich' }]} /> : null}
        </Modal>
      );
    case 'start':
      return (
        <Modal
          {...modal}
          title="Arbeit beginnen (P1)"
          footer={
            <>
              {footerStart}
              <Button disabled={locked} onClick={() => void run({ to: 'in_progress', lockedOut })}>
                Beginnen
              </Button>
            </>
          }
        >
          <p>Ist die Maschine gesichert bzw. freigeschaltet (§ 10 Abs. 3 BetrSichV)?</p>
          <div className="flex gap-2">
            <button type="button" className={`ad-tag ${lockedOut ? 'ad-tag-active' : ''}`} onClick={() => setLockedOut(true)}>
              Ja, gesichert
            </button>
            <button type="button" className={`ad-tag ${!lockedOut ? 'ad-tag-active' : ''}`} onClick={() => setLockedOut(false)}>
              Nicht nötig
            </button>
          </div>
        </Modal>
      );
    case 'waiting_part':
      return (
        <Modal
          {...modal}
          title="Wartet auf Ersatzteil"
          footer={
            <>
              {footerStart}
              <Button disabled={locked || note.trim().length < 3} onClick={() => void run({ to: 'waiting_part', waitingNote: note, inforItemNo: infor || null, waitingUntil: until || null })}>
                Speichern
              </Button>
            </>
          }
        >
          <Textarea label="Welches Teil?" required value={note} onChange={setNote} rows={2} />
          <Input label="Infor-Artikelnummer (optional)" value={infor} onChange={setInfor} />
          <Input label="Erwartet am (optional)" type="date" value={until} onChange={setUntil} />
        </Modal>
      );
    case 'waiting_external':
      return (
        <Modal
          {...modal}
          title="Wartet auf Dienstleister"
          footer={
            <>
              {footerStart}
              <Button
                disabled={locked || (!contactId && note.trim().length < 3)}
                onClick={() => void run({ to: 'waiting_external', waitingContactId: contactId || null, waitingNote: note || null, waitingUntil: until || null })}
              >
                Speichern
              </Button>
            </>
          }
        >
          <Select
            label="Dienstleister (aus Kontakten)"
            value={contactId}
            onChange={setContactId}
            options={[{ value: '', label: 'Keiner, nur Notiz' }, ...(contacts.data ?? []).map((c) => ({ value: c.id, label: `${c.company ?? c.name ?? c.email} (${c.email})` }))]}
          />
          <Textarea label="Notiz (Firma, Termin, Ansprechpartner)" value={note} onChange={setNote} rows={2} />
          <Input label="Termin (optional)" type="date" value={until} onChange={setUntil} />
        </Modal>
      );
    case 'waiting_reporter':
      return (
        <Modal
          {...modal}
          title="Rückfrage an den Melder"
          footer={
            <>
              {footerStart}
              <Button disabled={locked || question.trim().length < 3} onClick={() => void run({ to: 'waiting_reporter', question })}>
                Rückfrage senden
              </Button>
            </>
          }
        >
          <Textarea label="Frage" required value={question} onChange={setQuestion} rows={3} autoFocus />
          <p className="text-[13px] text-[var(--text-muted)]">Geht per Mail bzw. Push an den Melder. Seine Antwort setzt das Ticket wieder auf „In Arbeit“.</p>
        </Modal>
      );
    case 'resolved':
      return (
        <Modal
          {...modal}
          title="Behoben"
          footer={
            <>
              {footerStart}
              <Button
                disabled={locked || resolution.trim().length < 3}
                onClick={() =>
                  void run({
                    to: 'resolved',
                    causeCategory: cause,
                    resolutionText: resolution,
                    productionStop: stop,
                    machineRunningAt: stop || isUrgent(t.priority) ? fromLocalInput(runningAt) : null,
                    releasedByUserId: t.lockedOut ? releasedBy : null,
                    sparePartNote: sparePart || null,
                    inforItemNo: infor || null,
                  })
                }
              >
                Als behoben melden
              </Button>
            </>
          }
        >
          <Select label="Ursache" value={cause} onChange={(v) => setCause(v as CauseCategory)} options={CAUSE_CATEGORY.map((c) => ({ value: c, label: CAUSE_LABEL[c] }))} />
          <Textarea label="Maßnahme" required value={resolution} onChange={setResolution} rows={3} placeholder="Was wurde gemacht?" />
          <Checkbox label="Die Produktion stand still" checked={stop} onChange={setStop} />
          {stop || isUrgent(t.priority) ? (
            <Field label="Läuft wieder seit" required>
              <input className="ad-input" type="datetime-local" value={runningAt} onChange={(e) => setRunningAt(e.target.value)} />
            </Field>
          ) : null}
          {t.lockedOut ? (
            <Select label="Wieder freigegeben durch" value={releasedBy} onChange={setReleasedBy} options={workerOptions.length ? workerOptions : [{ value: meId, label: 'Ich' }]} />
          ) : null}
          <Input label="Ersatzteil (optional)" value={sparePart} onChange={setSparePart} />
          <Input label="Infor-Artikelnummer (optional)" value={infor} onChange={setInfor} />
          <p className="text-[13px] text-[var(--text-muted)]">Der Melder bekommt eine Mail mit „Läuft wieder“ / „Läuft nicht“. Ohne Widerspruch schließt das Ticket nach 5 Werktagen.</p>
        </Modal>
      );
    case 'rejected':
      return (
        <Modal
          {...modal}
          title="Ablehnen"
          footer={
            <>
              {footerStart}
              <Button
                variant="secondary"
                disabled={locked || (reason === 'duplicate' && dupNumber < 1000)}
                onClick={() => void run({ to: 'rejected', reason, comment, ...(reason === 'duplicate' ? { duplicateOfNumber: dupNumber } : {}) })}
              >
                Ablehnen
              </Button>
            </>
          }
        >
          <Select label="Grund" value={reason} onChange={(v) => setReason(v as RejectReason)} options={rejectReasons.map((r) => ({ value: r, label: REJECT_REASON_LABEL[r] }))} />
          {reason === 'duplicate' ? <Input label="Original-Ticket" required hint="Nummer des Original-Tickets, z. B. IH-1234" value={dupOf} onChange={setDupOf} placeholder="IH-1234" /> : null}
          <Textarea label="Begründung (optional, geht an den Melder, außer bei Spam)" value={comment} onChange={setComment} rows={3} />
        </Modal>
      );
    case 'reopen':
      return (
        <Modal
          {...modal}
          title="Wieder öffnen"
          footer={
            <>
              {footerStart}
              <Button disabled={locked || comment.trim().length < 3} onClick={() => void run({ to: 'reopen', comment })}>
                Wieder öffnen
              </Button>
            </>
          }
        >
          <Textarea label="Begründung" required value={comment} onChange={setComment} rows={3} />
        </Modal>
      );
    case 'in_progress':
      return (
        <Modal
          {...modal}
          title="Weiterarbeiten"
          footer={
            <>
              {footerStart}
              <Button disabled={locked} onClick={() => void run({ to: 'in_progress', comment: comment || null })}>
                Weiterarbeiten
              </Button>
            </>
          }
        >
          <Textarea label="Kommentar (optional)" value={comment} onChange={setComment} rows={2} />
        </Modal>
      );
  }
}

// ---------------------------------------------------------------------------
// Kommentar, Zeitleiste, Fotos, Steuerung
// ---------------------------------------------------------------------------

/** `@` vor dem Cursor: der angefangene Name (bis zum ersten Leerzeichen). */
const MENTION_AT_CARET = /(?:^|\s)@([\p{L}\p{N}.-]*)$/u;

function CommentBox({ ticket: t, onDone }: { ticket: TicketDetail; onDone: () => Promise<void> }) {
  const staff = t.permissions.canCommentInternal;
  const [body, setBody] = useState('');
  // Standard ist die Antwort an den Melder; „Intern“ wählt man bewusst, oder eine Erwähnung stellt es ein.
  const [visibility, setVisibility] = useState<'internal' | 'public'>('public');
  // Hat die Erwähnung auf „Intern“ gestellt, nicht der Nutzer? Dann gilt das nur für diesen Kommentar.
  const forcedInternal = useRef(false);
  // `active` -1: Noch keine Option gewählt, Enter und Tab behalten ihre gewohnte Wirkung.
  const [mention, setMention] = useState<{ query: string; active: number } | null>(null);
  const listId = useId();
  const textRef = useRef<HTMLTextAreaElement>(null);
  const workers = useQuery({ ...workersQuery(t.site?.id), enabled: staff });
  const me = useQuery(meQuery);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Erwähnen lassen sich die Instandhaltung des Werks, die Leitung und Hinzugezogene, nicht man selbst.
  const colleagues = [...new Map([...(workers.data ?? []), ...t.collaborators].map((u) => [u.id, { id: u.id, name: u.displayName }])).values()].filter(
    (c) => c.id !== me.data?.user.id,
  );
  const suggestions = mention === null ? [] : colleagues.filter((c) => c.name.toLowerCase().includes(mention.query.toLowerCase())).slice(0, 6);
  const activeSuggestion = mention ? suggestions[mention.active] : undefined;
  const mentioned = staff ? resolveMentions(body, colleagues) : [];
  // Auch `@Mia` ohne gewählten Vorschlag, oder solange die Kollegenliste fehlt, nennt dem Melder einen Namen.
  const hasMention = staff && (mentioned.length > 0 || hasAtWord(body));

  // Der Text trüge den Kollegennamen zum Melder. Fällt die Erwähnung weg, bleibt es bei „Intern“:
  // Was bis dahin intern geschrieben war, geht nicht ungefragt an den Melder.
  useEffect(() => {
    if (!hasMention || visibility === 'internal') return;
    forcedInternal.current = true;
    setVisibility('internal');
  }, [hasMention, visibility]);

  const choose = (next: 'internal' | 'public') => {
    forcedInternal.current = false;
    setVisibility(next);
  };

  const queryAtCaret = (value: string) => MENTION_AT_CARET.exec(value.slice(0, textRef.current?.selectionStart ?? value.length))?.[1] ?? null;

  const onText = (value: string) => {
    setBody(value);
    if (!staff) return;
    const query = queryAtCaret(value);
    setMention(query === null ? null : { query, active: -1 });
  };

  // Steht der Cursor nicht mehr im angefangenen Namen, träfe `pick` die falsche Stelle.
  const onCaretMove = () => {
    const query = queryAtCaret(textRef.current?.value ?? '');
    setMention((m) => (m?.query === query ? m : null));
  };

  const pick = (c: { id: string; name: string }) => {
    const el = textRef.current;
    const caret = el?.selectionStart ?? body.length;
    const before = body.slice(0, caret).replace(/@[\p{L}\p{N}.-]*$/u, `@${c.name} `);
    setBody(before + body.slice(caret));
    setMention(null);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(before.length, before.length);
    });
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!mention || !suggestions.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const last = suggestions.length - 1;
      const active = e.key === 'ArrowDown' ? (mention.active < last ? mention.active + 1 : 0) : mention.active > 0 ? mention.active - 1 : last;
      setMention({ ...mention, active });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMention(null);
    } else if ((e.key === 'Enter' || e.key === 'Tab') && activeSuggestion) {
      e.preventDefault();
      pick(activeSuggestion);
    }
  };

  const send = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await postJson<{ sentToReporter: boolean }>(`/api/tickets/${t.id}/comments`, {
        body,
        visibility,
        mentions: mentioned.map((m) => m.id),
      });
      setBody('');
      if (forcedInternal.current) choose('public');
      if (staff && visibility === 'public' && !res.sentToReporter) {
        setInfo(
          t.reporter.isSelf
            ? 'Sie haben dieses Ticket selbst gemeldet, deshalb geht keine Mail raus. Die Antwort steht im Ticket.'
            : 'Der Melder bekommt keine Mail (kein Microsoft-Konto oder keine Adresse). Die Antwort steht im Ticket.',
        );
      }
      await onDone();
    } catch (err) {
      setError(err);
    }
    setBusy(false);
  };

  return (
    <div className="ad-card space-y-3">
      <div>
        <Textarea
          label="Kommentar"
          value={body}
          onChange={onText}
          rows={3}
          inputRef={textRef}
          hint={staff ? 'Mit @ und Namen erwähnen Sie Kollegen. Wer Mail oder Push eingerichtet hat, wird benachrichtigt. Erwähnungen bleiben intern.' : undefined}
          onKeyDown={onKey}
          onSelect={onCaretMove}
          onBlur={() => setMention(null)}
          aria-autocomplete={staff ? 'list' : undefined}
          aria-controls={suggestions.length ? listId : undefined}
          aria-activedescendant={activeSuggestion ? `${listId}-${activeSuggestion.id}` : undefined}
        />
        {suggestions.length ? (
          <div id={listId} className="border border-t-0 border-[var(--border-default)] bg-white" role="listbox" aria-label="Kollegen erwähnen">
            {suggestions.map((c) => (
              <button
                key={c.id}
                id={`${listId}-${c.id}`}
                type="button"
                role="option"
                aria-selected={c === activeSuggestion}
                tabIndex={-1}
                className={`block w-full px-3 py-2 text-left text-[15px] ${c === activeSuggestion ? 'bg-steel-800 text-white' : 'hover:bg-[var(--surface-subtle)]'}`}
                // mousedown statt click: sonst verliert das Textfeld vorher Fokus und Cursorposition
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(c);
                }}
                // Screenreader lösen oft nur click aus, nach mousedown ist die Option schon weg.
                onClick={() => pick(c)}
              >
                @{c.name}
              </button>
            ))}
          </div>
        ) : null}
        <div aria-live="polite">
          {staff && body.includes('@') ? (
            <p className="mt-1.5 text-[14px] leading-snug text-[var(--text-muted)]">
              {hasMention && !mentioned.length
                ? 'Noch niemand erwähnt: Bitte den Vorschlag wählen, sonst wird niemand benachrichtigt.'
                : `Erwähnt: ${mentioned.map((c) => c.name).join(', ') || '-'}`}
            </p>
          ) : null}
        </div>
      </div>
      {staff ? (
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex gap-1.5">
            <button type="button" className={`ad-tag ${visibility === 'internal' ? 'ad-tag-active' : ''}`} onClick={() => choose('internal')}>
              Intern
            </button>
            <button
              type="button"
              className={`ad-tag disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-white ${visibility === 'public' ? 'ad-tag-active' : ''}`}
              disabled={hasMention}
              onClick={() => choose('public')}
            >
              An Melder
            </button>
          </div>
          <span className="text-[13px] text-[var(--text-muted)]" aria-live="polite">
            {hasMention
              ? 'Mit Erwähnung bleibt der Kommentar intern. „An Melder“ geht erst wieder, wenn die Erwähnung samt @ entfernt ist.'
              : visibility === 'public'
                ? 'Steht im Ticket. Melder mit Microsoft-Konto bekommen die Antwort auch per Mail.'
                : 'Nur die Instandhaltung sieht den Kommentar.'}
          </span>
        </div>
      ) : null}
      {info ? <Notice tone="warn">{info}</Notice> : null}
      {error ? <ErrorNote error={error} /> : null}
      <Button disabled={busy || !body.trim()} onClick={() => void send()}>
        Kommentar speichern
      </Button>
    </div>
  );
}

function eventTitle(e: TicketDetail['events'][number]): string {
  const data = (e.data ?? {}) as Record<string, unknown>;
  switch (e.kind) {
    case 'created':
      return 'Gemeldet';
    case 'status': {
      const to = data.to as TicketStatus | undefined;
      if (data.reopen && to) return `Wieder geöffnet: ${STATUS_LABEL[to]}`;
      if (data.via === 'site_move' && to) return `Werk gewechselt: ${STATUS_LABEL[to]}`;
      if (data.via === 'release' && to) return `Ohne Bearbeiter: ${STATUS_LABEL[to]}`;
      return to ? STATUS_LABEL[to] : 'Statuswechsel';
    }
    case 'comment':
      return data.appended ? 'Hinweis zur selben Störung' : e.visibility === 'internal' ? 'Interner Kommentar' : 'Kommentar';
    case 'mail_in':
      return data.initial ? 'Original-Mail' : data.auto ? 'Automatische Antwort' : 'Mail eingegangen';
    case 'mail_out':
      return 'Mail gesendet';
    case 'attachment':
      return 'Anhang';
    case 'assigned': {
      const to = e.assignedTo;
      if (to?.person) return `Zugewiesen an ${to.person}${to.team ? ` (${to.team})` : ''}`;
      if (to?.team) return `An Team ${to.team} gegeben`;
      return 'Zuweisung geändert';
    }
    case 'priority':
      return `Priorität: ${PRIORITY_LABEL[(data.to as Priority) ?? 'p3']}`;
    case 'machine':
      return 'Maschine geändert';
    case 'field':
      if (data.field === 'collaborators') {
        const who = e.personName ?? 'Kollege';
        return data.action === 'removed' ? `Nicht mehr hinzugezogen: ${who}` : `Hinzugezogen: ${who}`;
      }
      if (data.field === 'locked_out') {
        if (data.to) return 'Maschine gesichert';
        return data.from ? 'Sicherung aufgehoben' : 'Sicherung nicht nötig';
      }
      if (data.field === 'reply_blocked') return 'Mail an Melder wieder zugelassen';
      return 'Änderung';
    case 'system':
      return 'System';
    default:
      return e.kind;
  }
}

/** Mail-HTML im Sandbox-Frame: keine Skripte, CSP im Dokument, kein Einfluss aufs Layout (§8.5). */
function MailFrame({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(160);
  const doc = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src 'self' data:; style-src 'unsafe-inline'"><style>body{font:14px Arial,sans-serif;color:#333e45;margin:8px;word-break:break-word}img{max-width:100%;height:auto}</style></head><body>${html}</body></html>`;
  return (
    <iframe
      ref={ref}
      title="E-Mail-Inhalt"
      sandbox="allow-same-origin"
      srcDoc={doc}
      className="w-full border border-[var(--border-default)] bg-white"
      style={{ height }}
      onLoad={() => {
        const h = ref.current?.contentDocument?.body?.scrollHeight;
        if (h) setHeight(Math.min(Math.max(h + 24, 80), 600));
      }}
    />
  );
}

function FullMail({ ticketId, mailId }: { ticketId: string; mailId: string }) {
  const mail = useQuery({
    queryKey: ['mail', mailId],
    queryFn: () => getJson<{ bodyHtml: string | null }>(`/api/tickets/${ticketId}/mails/${mailId}`),
  });
  if (mail.isPending) return <Spinner />;
  if (mail.isError) return <ErrorNote error={mail.error} />;
  return mail.data.bodyHtml ? <MailFrame html={mail.data.bodyHtml} /> : <p className="text-sm">Der Mail-Inhalt wurde nach Ablauf der Frist gelöscht.</p>;
}

function Timeline({ ticket: t }: { ticket: TicketDetail }) {
  const [showMail, setShowMail] = useState<Record<string, boolean>>({});
  const [showFull, setShowFull] = useState<Record<string, boolean>>({});
  const events = [...t.events].reverse();
  return (
    <section className="space-y-2">
      <h2 className="ad-section-title">Verlauf</h2>
      <ol className="space-y-2">
        {events.map((e) => (
          <li key={e.id} className={`border-l-4 bg-white px-4 py-3 ${e.visibility === 'internal' ? 'border-steel-300' : 'border-steel-800'}`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-semibold text-steel-900">{eventTitle(e)}</span>
              <span className="text-[var(--text-muted)]">{e.actor}</span>
              <span className="ml-auto text-[13px] text-[var(--text-muted)]">{fmtDateTime(e.createdAt)}</span>
            </div>
            {e.mail ? (
              <div className="mt-1 text-[13px] text-[var(--text-muted)]">
                {e.mail.from ? `Von ${e.mail.from}` : ''} {e.mail.subject ? `· ${e.mail.subject}` : ''}
              </div>
            ) : null}
            {e.bodyText && !(e.bodyHtml && showMail[e.id]) ? <p className="mt-1.5 whitespace-pre-wrap text-[15px] text-steel-800">{e.bodyText}</p> : null}
            {e.bodyHtml ? (
              <div className="mt-2">
                <button type="button" className="cursor-pointer text-[13px] text-arnold-red" onClick={() => setShowMail((s) => ({ ...s, [e.id]: !s[e.id] }))}>
                  {showMail[e.id] ? 'Formatierte Mail ausblenden' : 'Formatierte Mail anzeigen'}
                </button>
                {showMail[e.id] ? <MailFrame html={e.bodyHtml} /> : null}
              </div>
            ) : null}
            {e.mail?.hasFullHtml ? (
              <div className="mt-1">
                <button type="button" className="cursor-pointer text-[13px] text-steel-600 underline" onClick={() => setShowFull((s) => ({ ...s, [e.id]: !s[e.id] }))}>
                  {showFull[e.id] ? 'Vollständige Mail ausblenden' : 'Vollständige Mail mit Verlauf'}
                </button>
                {showFull[e.id] ? <FullMail ticketId={t.id} mailId={e.mail.id} /> : null}
              </div>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

function InternalBadge({ file }: { file: TicketDetail['attachments'][number] }) {
  return file.visibility === 'internal' ? (
    <span className="shrink-0">
      <Badge tone="steel">Intern</Badge>
    </span>
  ) : null;
}

function Photos({ ticket: t, onChange }: { ticket: TicketDetail; onChange: () => Promise<void> }) {
  const staff = t.permissions.canCommentInternal;
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [view, setView] = useState<TicketDetail['attachments'][number] | null>(null);
  const remove = useAction();
  // Anders als beim Kommentar ist „An Melder“ hier die bewusste Wahl: Angebote und Prüfberichte bleiben intern.
  const [visibility, setVisibility] = useState<Visibility>('internal');
  const files = t.attachments;

  const open = (file: TicketDetail['attachments'][number]) => {
    remove.setError(null);
    setView(file);
  };

  const upload = async (list: FileList | null) => {
    if (!list) return;
    setBusy(true);
    setError(null);
    try {
      const picked = Array.from(list);
      // Vor dem ersten Upload: Bricht die Auswahl mittendrin ab, ist ein Teil oben, und die Seite zeigt ihn nicht.
      picked.filter((f) => !f.type.startsWith('image/')).forEach(assertDocumentSize);
      for (const f of picked) {
        const blob = f.type.startsWith('image/') ? await resizePhoto(f) : f;
        await uploadFile(`/api/tickets/${t.id}/attachments`, blob, f.type.startsWith('image/') ? 'foto.jpg' : f.name, staff ? { visibility } : {});
      }
      await onChange();
    } catch (err) {
      setError(err);
    }
    setBusy(false);
  };

  return (
    <section className="ad-card space-y-3">
      <h2 className="ad-section-title">Fotos und Anhänge</h2>
      {files.length ? (
        <div className="grid grid-cols-3 gap-2">
          {files.map((a) =>
            a.isImage ? (
              <button key={a.id} type="button" className="relative cursor-zoom-in" onClick={() => open(a)}>
                <img src={a.thumbUrl ?? a.url} alt={a.filename} loading="lazy" className="aspect-square w-full border border-[var(--border-default)] object-cover" />
                <span className="absolute top-1 left-1">
                  <InternalBadge file={a} />
                </span>
              </button>
            ) : isViewableFile(a.contentType) ? (
              <button key={a.id} type="button" className="ad-link col-span-3 flex min-h-11 cursor-pointer items-center gap-2 text-left text-[15px]" onClick={() => open(a)}>
                <FileText size={22} className="shrink-0 text-steel-700" aria-hidden />
                <span className="truncate">{a.filename}</span>
                <InternalBadge file={a} />
              </button>
            ) : (
              <a key={a.id} href={downloadUrl(a.url)} className="ad-link col-span-3 flex min-h-11 items-center gap-2 text-[15px]">
                <FileText size={22} className="shrink-0 text-steel-700" aria-hidden />
                <span className="truncate">{a.filename}</span>
                <InternalBadge file={a} />
              </a>
            ),
          )}
        </div>
      ) : (
        <p className="text-sm text-[var(--text-muted)]">Noch keine.</p>
      )}
      {error ? <ErrorNote error={error} /> : null}
      {/* Der Dialog kann zu sein, bis die Antwort kommt. Auch dann muss der Fehler zu sehen sein. */}
      {remove.error ? <ErrorNote error={remove.error} /> : null}
      {t.permissions.canUpload ? (
        <>
          {staff ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="flex gap-1.5">
                <Tag active={visibility === 'internal'} onClick={() => setVisibility('internal')}>
                  Intern
                </Tag>
                <Tag active={visibility === 'public'} onClick={() => setVisibility('public')}>
                  An Melder
                </Tag>
              </div>
              <span className="text-[13px] text-[var(--text-muted)]">
                {visibility === 'public' ? 'Melder mit Anmeldung sehen die Datei im Ticket. Per Mail geht sie nicht raus.' : 'Nur die Instandhaltung sieht die Datei.'}
              </span>
            </div>
          ) : null}
          <Button variant="outline" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? 'Wird hochgeladen …' : 'Foto hinzufügen'}
          </Button>
          <input
            ref={input}
            type="file"
            className="hidden"
            accept={staff ? 'image/jpeg,image/png,image/webp,application/pdf' : 'image/jpeg,image/png,image/webp'}
            capture="environment"
            multiple
            onChange={(e) => {
              void upload(e.target.files);
              e.target.value = '';
            }}
          />
          <p className="text-[13px] text-[var(--text-muted)]">Bitte nur die Maschine fotografieren, keine Personen.</p>
        </>
      ) : null}
      <FileViewer
        file={view}
        onClose={() => setView(null)}
        error={remove.error}
        actions={
          t.permissions.canDeleteAttachments && view ? (
            <Button
              variant="outline"
              size="sm"
              disabled={remove.busy}
              onClick={() => {
                const what = view.isImage ? 'Foto' : 'Datei';
                if (!window.confirm(`${what} löschen? Sie wird nach 30 Tagen endgültig entfernt.`)) return;
                void remove.run(async () => {
                  await deleteJson(`/api/attachments/${view.id}`);
                  await onChange();
                  setView(null);
                });
              }}
            >
              {view.isImage ? 'Foto löschen' : 'Datei löschen'}
            </Button>
          ) : undefined
        }
      />
    </section>
  );
}

type Control = 'priority' | 'lockout' | 'machine';

function WorkerControls({ ticket: t, onChange }: { ticket: TicketDetail; onChange: () => Promise<void> }) {
  const [priority, setPriority] = useState<Priority>(t.priority);
  const [reason, setReason] = useState('');
  const [machineOpen, setMachineOpen] = useState(false);
  const [machine, setMachine] = useState<MachineListItem | null>(null);
  const [lastControl, setLastControl] = useState<Control>('priority');
  const { run, busy, error } = useAction();
  useEffect(() => setPriority(t.priority), [t.priority]);
  const leavingP1 = t.priority === 'p1';

  const call = async (control: Control, body: Record<string, unknown>) => {
    let saved = false;
    await run(async () => {
      setLastControl(control);
      await postJson(`/api/tickets/${t.id}/${control}`, { ...body, version: t.version }).finally(onChange);
      saved = true;
    });
    return saved;
  };

  const savePriority = async () => {
    if (await call('priority', { priority, reason: leavingP1 ? reason : null })) setReason('');
  };

  const saveMachine = async () => {
    if (await call('machine', { machineId: machine?.id })) setMachineOpen(false);
  };

  // Steht neben dem Block des Elements, nicht darin: Den Block kann das Neuladen nach einem Konflikt wegnehmen.
  const failure = (control: Control) => (error && lastControl === control ? <ErrorNote error={error} /> : null);

  // Auch die Karte bleibt für ihren Fehler stehen, wenn das Neuladen alle Rechte daran nimmt.
  if (!t.permissions.canChangePriority && !t.permissions.canSetLockout && !t.permissions.canChangeMachine && !error) return null;

  return (
    <section className="ad-card space-y-4">
      <h2 className="ad-section-title">Steuerung</h2>
      {t.permissions.canChangePriority ? (
        <div className="space-y-2">
          <Select label="Priorität" value={priority} onChange={(v) => setPriority(v as Priority)} options={PRIORITY.map((p) => ({ value: p, label: PRIORITY_LABEL[p] }))} />
          {priority !== t.priority ? (
            <>
              {leavingP1 ? <Input label="Begründung" required hint="Eine Herabstufung von P1 braucht eine Begründung." value={reason} onChange={setReason} /> : null}
              <Button size="sm" disabled={busy || (leavingP1 && !reason.trim())} onClick={() => void savePriority()}>
                Priorität speichern
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
      {failure('priority')}
      {t.permissions.canSetLockout ? (
        <Checkbox label="Maschine gesichert (LOTO)" checked={t.lockedOut} disabled={busy} onChange={(v) => void call('lockout', { lockedOut: v })} />
      ) : null}
      {failure('lockout')}
      {t.permissions.canChangeMachine ? (
        machineOpen ? (
          <div className="space-y-2">
            <MachinePicker ticket={t} value={machine} onChange={setMachine} />
            <div className="flex gap-2">
              <Button size="sm" disabled={busy || !machine} onClick={() => void saveMachine()}>
                Übernehmen
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setMachineOpen(false)}>
                Abbrechen
              </Button>
            </div>
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setMachine(null);
              setMachineOpen(true);
            }}
          >
            Maschine ändern
          </Button>
        )
      ) : null}
      {failure('machine')}
    </section>
  );
}
